import React from "react";
import { ApiContext } from "../common/context/ApiContext";
import { useLocation, useHistory } from "react-router-dom";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { Tab, Tabs } from "react-bootstrap";
import styled from "styled-components";
import { BackNav, PageContainer } from "../common/CommonStyles";
import { SchoolDashboard, SchoolDashboardError } from "../common/api";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import {
  AnalyticsContext,
  SchoolDashboardDistrictPageViewEvent,
  SchoolDashboardLessonExecutionPageViewEvent,
  SchoolDashboardLessonPageViewEvent,
  SchoolDashboardSchoolPageViewEvent,
  SchoolDashboardTeacherPageViewEvent,
  SchoolDashboardUserPageViewEvent,
} from "../common/context/AnalyticsContext";
import { AssessmentsTab } from "../components/dashboard/AssessmentsTab";
import { AdminToolsTab } from "../components/dashboard/AdminToolsTab";
import { LessonExecutionAnalyticsTab } from "../components/dashboard/LessonExecutionAnalyticsTab";
import { LessonAnalyticsTab } from "../components/dashboard/LessonAnalyticsTab";
import { GenericAnalyticsTab } from "../components/dashboard/GenericAnalyticsTab";
import { SchoolTeachersTab } from "../components/dashboard/SchoolTeachersTab";
import { TeacherStudentsTab } from "../components/dashboard/TeacherStudentsTab";
import { Breadcrumbs } from "../components/dashboard/Breadcrumbs";
import { SingleUserAnalyticsTab } from "../components/dashboard/SingleUserAnalyticsTab";
import { AssignmentsTab } from "../components/dashboard/AssignmentsTab";
import { DistrictAnalyticsTab } from "../components/dashboard/DistrictAnalyticsTab";
import { SchoolsTable } from "../components/dashboard/SchoolsTable";
import {
  COLOR_SURFACE_CONTAINER,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_WHITE,
} from "../common/colors";
import { ELEVATION_1 } from "../common/elevation";
import { useIsEmbedded } from "../common/embed";
import {
  SPACE_3XS,
  SPACE_4XL,
  SPACE_LG,
  SPACE_XL,
  SPACE_XS,
} from "../common/spacing";

const DashboardTabs = styled.div<{ $hidePills: boolean }>`
  margin: ${({ $hidePills }) => ($hidePills ? SPACE_XL : SPACE_LG)}px 0
    ${SPACE_4XL}px;
  .nav-pills {
    display: ${({ $hidePills }) => ($hidePills ? "none" : "inline-flex")};
    flex-wrap: nowrap;
    gap: ${SPACE_3XS}px;
    max-width: 100%;
    margin-bottom: ${SPACE_XL}px;
    padding: ${SPACE_XS}px;
    overflow-x: auto;
    border-radius: 100px;
    background-color: ${COLOR_SURFACE_CONTAINER};
  }
  .nav-pills .nav-link {
    padding: 7px 14px;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    white-space: nowrap;
    --bs-nav-pills-border-radius: 100px;
    --bs-nav-link-color: ${COLOR_TEXT_SECONDARY};
    --bs-nav-link-hover-color: ${COLOR_TEXT_PRIMARY};
  }
  .nav-pills .nav-link.active {
    font-weight: 600;
    box-shadow: ${ELEVATION_1};
    --bs-nav-pills-link-active-bg: ${COLOR_WHITE};
    --bs-nav-pills-link-active-color: ${COLOR_TEXT_PRIMARY};
  }
`;

const DASHBOARD_ERROR_KEYS: Record<SchoolDashboardError["error"], string> = {
  sync_pending: "dashboard_error_sync_pending",
  unsupported_account: "dashboard_error_unsupported_account",
};

export const SchoolDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const isEmbedded = useIsEmbedded();
  const { setCurrentTab: setCurrentNavTab } = React.useContext(TabsContext);
  const { getSchoolDashboard } = React.useContext(ApiContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const [account, setAccount] = React.useState<Account>();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const dashboardFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);
  const dashboardFetchedForSearch = React.useRef<string | null>(null);
  const fetchJobForSearch = React.useRef<string | null>(null);
  const [dashboard, setDashboard] = React.useState<
    SchoolDashboard | SchoolDashboardError | null
  >(null);
  const [currentTab, setCurrentTab] = React.useState("analytics-contents");
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    setCurrentNavTab(TabEnum.Dashboard);
  }, [setCurrentNavTab]);

  const logDashboardTabView = React.useCallback(
    // MAKE SURE event names and properties match those in the clever_school_dashboard.html
    (dashboard: SchoolDashboard, tabDivId: string) => {
      if (dashboard.is_at_district_level) {
        const tabName =
          tabDivId === "analytics-contents" ? "Analytics" : tabDivId;
        logEvent(
          new SchoolDashboardDistrictPageViewEvent(
            tabName,
            dashboard.district_id
          )
        );
      } else if (dashboard.is_at_school_level) {
        const tabName =
          tabDivId === "analytics-contents"
            ? "Analytics"
            : dashboard.secondary_tab_name || tabDivId;
        logEvent(
          new SchoolDashboardSchoolPageViewEvent(
            tabName,
            dashboard.district_id,
            dashboard.school_id
          )
        );
      } else if (dashboard.is_at_teacher_level) {
        const tabName =
          tabDivId === "analytics-contents"
            ? "Analytics"
            : tabDivId === "assessments-contents"
              ? "Assessments"
              : tabDivId === "assignments-contents"
                ? "Assignments"
                : dashboard.secondary_tab_name || tabDivId;
        logEvent(
          new SchoolDashboardTeacherPageViewEvent(
            tabName,
            dashboard.district_id,
            dashboard.school_id,
            dashboard.teacher_id
          )
        );
      } else if (dashboard.is_at_lesson_level) {
        const tabName =
          tabDivId === "analytics-contents"
            ? "Analytics"
            : dashboard.secondary_tab_name || tabDivId;
        logEvent(
          new SchoolDashboardLessonPageViewEvent(
            tabName,
            dashboard.district_id,
            dashboard.school_id,
            dashboard.teacher_id,
            dashboard.lesson_id,
            dashboard.custom_flashpack_id,
            dashboard.lesson_title
          )
        );
      } else if (dashboard.is_at_lesson_execution_level) {
        const tabName =
          tabDivId === "analytics-contents" ? "Analytics" : tabDivId;
        logEvent(
          new SchoolDashboardLessonExecutionPageViewEvent(
            tabName,
            dashboard.district_id,
            dashboard.school_id,
            dashboard.teacher_id,
            dashboard.student_user_id,
            dashboard.lesson_id,
            dashboard.custom_flashpack_id,
            dashboard.lesson_title,
            dashboard.lesson_execution_id
          )
        );
      } else if (dashboard.is_at_school_group_level) {
        // no analytics event defined for school group level yet
      } else if (dashboard.is_at_single_user_level) {
        const tabName =
          tabDivId === "analytics-contents"
            ? "Analytics"
            : dashboard.secondary_tab_name || tabDivId;
        logEvent(
          new SchoolDashboardUserPageViewEvent(
            tabName,
            dashboard.district_id,
            dashboard.school_id,
            dashboard.teacher_id,
            dashboard.student_user_id
          )
        );
      }
    },
    [logEvent]
  );

  React.useEffect(() => {
    // Whenever dashboard is reloaded, assume we are on the analytics tab.
    // WARNING: this must change when we start linking directly to tabs.
    if (dashboard && !("error" in dashboard)) {
      logDashboardTabView(dashboard, "analytics-contents");
    }
  }, [logDashboardTabView, dashboard]);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    (async () => {
      if (!account) {
        return;
      }
      if (
        (account.userCurrentVersion !==
          dashboardFetchedForUserVersion.current &&
          account.userCurrentVersion !== fetchJobForUserVersion.current) ||
        (search !== dashboardFetchedForSearch.current &&
          search !== fetchJobForSearch.current)
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        const expectedSearch = (fetchJobForSearch.current = search);
        setDashboard(await getSchoolDashboard(search));
        if (
          expectedVersion !== fetchJobForUserVersion.current ||
          expectedSearch !== fetchJobForSearch.current
        ) {
          return;
        }

        dashboardFetchedForUserVersion.current = fetchJobForUserVersion.current;
        dashboardFetchedForSearch.current = fetchJobForSearch.current;
        stopLoading();
      }
    })();
  }, [account, search, getSchoolDashboard, startLoading, stopLoading]);

  const navigate = React.useCallback(
    (url, dont_change_tab?) => {
      return (event) => {
        if (url !== false && url !== undefined) {
          if (!dont_change_tab) {
            setCurrentTab("analytics-contents");
          }
          history.push({
            search: url,
          });
        }
        event.preventDefault();
      };
    },
    [history]
  );

  if (!dashboard) {
    return <></>;
  }

  if ("error" in dashboard) {
    return (
      <PageContainer>
        <div className="text-center text-muted mt-5">
          {t(DASHBOARD_ERROR_KEYS[dashboard.error])}
        </div>
      </PageContainer>
    );
  }

  // Individual Tab components must be immediate children of Tabs,
  // they cannot be rendered inside a fragment or a div,
  // that's why each level is an array of Tabs.
  const tabs = dashboard.is_at_school_group_level
    ? [
        <Tab
          key="analytics"
          eventKey="analytics-contents"
          title={t("dashboard_overview")}
        >
          <GenericAnalyticsTab dashboard={dashboard} navigate={navigate} />
        </Tab>,
        <Tab
          key="schools"
          eventKey="schools-contents"
          title={t("school_list_schools_title")}
        >
          <SchoolsTable schools={dashboard.schools} navigate={navigate} />
        </Tab>,
        <Tab
          key="students"
          eventKey="classes-contents"
          title={dashboard.secondary_tab_name}
        >
          <TeacherStudentsTab dashboard={dashboard} navigate={navigate} />
        </Tab>,
      ]
    : dashboard.is_at_district_level
      ? [
          <Tab
            key="analytics"
            eventKey="analytics-contents"
            title={t("dashboard_overview")}
          >
            <DistrictAnalyticsTab dashboard={dashboard} navigate={navigate} />
          </Tab>,
        ]
      : dashboard.is_at_school_level
        ? [
            <Tab
              key="analytics"
              eventKey="analytics-contents"
              title={t("dashboard_overview")}
            >
              <GenericAnalyticsTab dashboard={dashboard} navigate={navigate} />
            </Tab>,
            <Tab
              key="classes"
              eventKey="classes-contents"
              title={dashboard.secondary_tab_name}
            >
              <SchoolTeachersTab dashboard={dashboard} navigate={navigate} />
            </Tab>,
            !isEmbedded && (
              <Tab
                key="admin_tools"
                eventKey="admin-tools-contents"
                title={t("dashboard_admin_tools")}
              >
                <AdminToolsTab dashboard={dashboard} />
              </Tab>
            ),
          ]
        : dashboard.is_at_teacher_level
          ? [
              <Tab
                key="analytics"
                eventKey="analytics-contents"
                title={t("dashboard_overview")}
              >
                <GenericAnalyticsTab
                  dashboard={dashboard}
                  navigate={navigate}
                />
              </Tab>,
              <Tab
                key="students"
                eventKey="classes-contents"
                title={dashboard.secondary_tab_name}
              >
                <TeacherStudentsTab dashboard={dashboard} navigate={navigate} />
              </Tab>,
              <Tab
                key="assessments"
                eventKey="assessments-contents"
                title={t("dashboard_assessments")}
              >
                <AssessmentsTab
                  assessments={dashboard.assessments}
                  navigate={navigate}
                />
              </Tab>,
              <Tab
                key="assignments"
                eventKey="assignments-contents"
                title={t("dashboard_assignments")}
              >
                <AssignmentsTab
                  assignments={dashboard.assignments}
                  navigate={navigate}
                />
              </Tab>,
            ]
          : dashboard.is_at_single_user_level
            ? [
                <Tab
                  key="analytics"
                  eventKey="analytics-contents"
                  title={t("dashboard_overview")}
                >
                  <SingleUserAnalyticsTab
                    dashboard={dashboard}
                    navigate={navigate}
                  />
                </Tab>,
              ]
            : dashboard.is_at_lesson_level
              ? [
                  <Tab
                    key="analytics"
                    eventKey="analytics-contents"
                    title={t("dashboard_overview")}
                  >
                    <LessonAnalyticsTab
                      dashboard={dashboard}
                      navigate={navigate}
                    />
                  </Tab>,
                ]
              : dashboard.is_at_lesson_execution_level
                ? [
                    <Tab
                      key="analytics"
                      eventKey="analytics-contents"
                      title={t("dashboard_overview")}
                    >
                      <LessonExecutionAnalyticsTab dashboard={dashboard} />
                    </Tab>,
                  ]
                : [];

  return (
    <PageContainer>
      {!isEmbedded && <BackNav label={t("generic_nav_back")} />}
      <Breadcrumbs dashboard={dashboard} navigate={navigate} />
      <DashboardTabs $hidePills={tabs.filter(Boolean).length < 2}>
        <Tabs
          variant="pills"
          activeKey={currentTab}
          onSelect={(key) => {
            setCurrentTab(key as string);
            logDashboardTabView(dashboard, key as string);
          }}
        >
          {tabs}
        </Tabs>
      </DashboardTabs>
    </PageContainer>
  );
};
